// @flow
import React from 'react';
import { useIsEstimateRenewal } from '@src/custom-hooks/useIsEstimateRenewal';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { If } from '@presentational/If';
import { ButtonWithPopup } from '@presentational/ButtonWithPopup';
import { useImportRenewalDiscountsMutation } from '@src/service-definitions/estimates';
import { toastError, toastSuccess } from '@presentational/notifications/utils';
import { useGlobalDiscountProvidedByStatus } from '@src/custom-hooks/useGlobalDiscountProvidedByStatus';
import { useEstimateVersion } from '@src/custom-hooks/useEstimateVersion';
import { ImportDiscountsNotification } from '@components/renewal-automation/ImportDiscountsNotification';
import {
  getImportButtonTooltipText,
  getIfImportButtonShouldBeDisabled,
} from '@components/renewal-automation/utils';

const getImportButtonHandler = (estimateId, estimateVersion, importMLDFunc) => {
  return async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { error, data } = await importMLDFunc({
      estimateId,
      payload: {
        estimate_version: estimateVersion,
      },
    });

    if (error) {
      toastError(error);
    } else {
      const succeededMetrics = data?.succeeded_metrics ?? [];
      const failedMetrics = data?.failed_metrics ?? [];

      if (failedMetrics.length > 0) {
        toastError(
          null, // error is null as we the status is 20*
          <ImportDiscountsNotification
            failedMetrics={failedMetrics}
            succeededMetrics={succeededMetrics}
          />
        );
      } else {
        toastSuccess('Successfully imported renewal discounts!');
      }
    }
  };
};

export const ImportDiscountsButton = ({ disabledFromParent }) => {
  const { estimateId } = useParams();
  const { values } = useFormikContext();
  const isRenewal = useIsEstimateRenewal();
  const estimateVersion = useEstimateVersion();
  const { isGlobalDiscountImported } = useGlobalDiscountProvidedByStatus();
  const [importMLDFunc] = useImportRenewalDiscountsMutation();

  const { shouldImportButtonBeDisabledBecauseOfMLD, shouldImportButtonBeDisabledBecauseOfGD } =
    getIfImportButtonShouldBeDisabled(values, isGlobalDiscountImported);

  const importDiscountsHandler = getImportButtonHandler(estimateId, estimateVersion, importMLDFunc);

  return (
    <If test={isRenewal}>
      <ButtonWithPopup
        compact={true}
        disabled={
          disabledFromParent ||
          shouldImportButtonBeDisabledBecauseOfMLD ||
          shouldImportButtonBeDisabledBecauseOfGD
        }
        onClick={importDiscountsHandler}
        popupContent={getImportButtonTooltipText(
          shouldImportButtonBeDisabledBecauseOfMLD,
          shouldImportButtonBeDisabledBecauseOfGD
        )}
        size="mini"
        text="Import Discounts"
      />
    </If>
  );
};
